export enum PatientStatus {
  WAITING = 'WAITING',
  PREPARATION = 'PREPARATION',
  ROOM_ENTRY = 'ROOM_ENTRY',
  ANESTHESIA = 'ANESTHESIA',
  SURGERY = 'SURGERY',
  RECOVERY = 'RECOVERY',
  EXIT = 'EXIT',
}

export enum SurgerySubStatus {
  INCISION = 'INCISION',     // Starts at 15%
  PROCEDURE = 'PROCEDURE',   // Starts at 30%, variable up to 85%
  CLOSING = 'CLOSING',       // Starts at 90%
  FINISHED = 'FINISHED'      // Starts at 95%
}

export enum SurgeryCondition {
  NORMAL = 'NORMAL',
  DELAYED = 'DELAYED',
  EXTENDED = 'EXTENDED'
}

export interface StatusLog {
  status: PatientStatus;
  subStatus?: SurgerySubStatus;
  condition?: SurgeryCondition;
  timestamp: string; // ISO string
  updatedBy: string;
}

export interface HospitalBranding {
  primaryColor: string;
  secondaryColor: string;
  logoUrl?: string; // Can be a URL or Base64 string
  logoDarkUrl?: string; // Logo for Dark Theme
  backgroundColor?: '#ffffff' | '#f2f2f7'; // Restricted background options
}

export interface PromotionalBanner {
  id: string;
  text: string;           // E.g., "Visita nuestra cafetería"
  icon?: string;          // E.g., "☕️" or a URL
  displayTimeSec: number; // Seconds to display (e.g., 15)
  isActive: boolean;
}

// ============================================================
// 📺 AD SYSTEM — Smart Advertising
// ============================================================

export type AdType = 'BANNER_TEXT' | 'BANNER_IMAGE' | 'TICKER' | 'FULLSCREEN_SLIDE';
export type AdTarget = 'ALL' | 'TV_ONLY' | 'MOBILE_ONLY';

// Layout options for the live patient board (TV display)
export type BoardLayout = 'LIST' | 'GRID_2COL' | 'SIDEBAR_AD' | 'FOCUS_SINGLE' | 'TICKER_ONLY';


export interface AdSchedule {
  daysOfWeek: number[]; // [0, 1, 2, 3, 4, 5, 6] donde 0 = Domingo, 1 = Lunes... Si está vacío, aplica todos los días.
  // Opción A: Intervalo Abierto
  startTime?: string;   // Formato 'HH:MM' (ej: '08:00')
  endTime?: string;     // Formato 'HH:MM' (ej: '11:30')
  // Opción B: Inyección Exacta
  // NUEVO: Rango de fechas (campaña)
  startDate?: string;   // Formato 'YYYY-MM-DD' — primera fecha activa
  endDate?: string;     // Formato 'YYYY-MM-DD' — última fecha activa
}

export interface AdAsset {
  id: string;
  name?: string;             // Nombre de la campaña (Ej: "Promo Verano Cafetería")
  type: AdType;
  // Para BANNER_TEXT / TICKER
  text?: string;
  icon?: string;
  textSize?: 'sm' | 'md' | 'lg' | 'xl';
  // Para BANNER_IMAGE / FULLSCREEN_SLIDE (URL de R2)
  imageUrl?: string;
  imageAlt?: string;
  mediaType?: 'image' | 'video'; // Para detectar si es imagen o video
  youtubeUrl?: string;           // URL de YouTube (video o playlist)
  // Configuración de display
  displayTimeSec: number;      // Duración en pantalla para rotación
  frequencyPerHour?: number;
  target: AdTarget;            // En qué pantallas se muestra
  isActive: boolean;
  priority: number;            // Orden de aparición (1 = primero)
  createdAt?: string;
  schedule?: AdSchedule;       // Reglas de programación por días y horas
  targetGroups?: string[];     // Array de ubicaciones (Ej: ['PB', 'Piso 2']). Si está vacío/nil, a todas.
  linkUrl?: string;            // URL destino al hacer click en celulares (Ej: 'https://hospital.com/promo')
  showDuringSurgery?: boolean; // Mostrar anuncio en celulares durante la etapa de Cirugía Activa (default: true)
  
  // --- Control Corporativo (Group Admin) ---
  isGlobal?: boolean;          // True si fue creado por el corporativo (GroupAdmin)
  groupId?: string;            // ID de la cadena/grupo al que pertenece este anuncio global
  targetHospitals?: string[];  // IDs de los hospitales donde se debe mostrar. Si está vacío, a toda la red.
  
  // --- Control por Pantalla (Retail Media) ---
  targetScreenId?: string;     // ID específico de la pantalla si este anuncio es exclusivo para una TV
}

export const isAssetActiveRightNow = (asset: AdAsset, checkTime?: Date, currentScreenGroup?: string, currentScreenId?: string): boolean => {
  if (!asset.isActive) return false;
  if (asset.id === 'draft-asset-preview') return true; // Siempre visualizar en el Live Preview

  const now = checkTime instanceof Date ? checkTime : new Date();
  const currentDay = now.getDay();

  if (asset.schedule) {

    // 0. Verificar Rango de Fechas (Campañas)
    if (asset.schedule.startDate) {
      // Usar fecha local del hospital (o del sistema) en formato YYYY-MM-DD
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;

      if (todayStr < asset.schedule.startDate) return false;
      if (asset.schedule.endDate && todayStr > asset.schedule.endDate) return false;
    }

    // 1. Verificar Día de la Semana
    if (asset.schedule.daysOfWeek && asset.schedule.daysOfWeek.length > 0) {
      if (!asset.schedule.daysOfWeek.includes(currentDay)) return false;
    }

    // 2. Verificar Opción B: Horas Exactas
    if (asset.schedule.exactTimes && asset.schedule.exactTimes.length > 0) {
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTotalMinutes = currentHours * 60 + currentMinutes;

      // Margen de tolerancia: El asset se considerará activo si la hora actual está dentro de [HoraExacta, HoraExacta + 2 minutos)
      const TOLERANCE_MINUTES = 2;

      const matchesExactTime = asset.schedule.exactTimes.some(timeStr => {
        const [h, m] = timeStr.split(':').map(Number);
        const exactTotalMinutes = h * 60 + m;
        return currentTotalMinutes >= exactTotalMinutes && currentTotalMinutes < (exactTotalMinutes + TOLERANCE_MINUTES);
      });

      if (!matchesExactTime) return false;
    }
    // 3. Verificar Opción A: Hora de Inicio y Fin
    else if (asset.schedule.startTime && asset.schedule.endTime) {
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();
      const currentTotalMinutes = currentHours * 60 + currentMinutes;

      const [startH, startM] = asset.schedule.startTime.split(':').map(Number);
      const startTotalMinutes = startH * 60 + startM;

      const [endH, endM] = asset.schedule.endTime.split(':').map(Number);
      let endTotalMinutes = endH * 60 + endM;

      // Manejar horarios cruzados por la medianoche (ej: 22:00 a 06:00)
      if (endTotalMinutes < startTotalMinutes) {
        if (currentTotalMinutes >= startTotalMinutes || currentTotalMinutes <= endTotalMinutes) {
          return true;
        } else {
          return false;
        }
      } else {
        if (currentTotalMinutes < startTotalMinutes || currentTotalMinutes > endTotalMinutes) return false;
      }
    }
  } // Closing bracket for if (asset.schedule)

  // 4. Verificar Frecuencia por Hora (si aplica)
  if (asset.frequencyPerHour && asset.frequencyPerHour > 0) {
    const currentMinutes = now.getMinutes();
    const intervalInMinutes = Math.floor(60 / asset.frequencyPerHour);
    // Checamos si el minuto actual es múltiplo del intervalo
    if (currentMinutes % intervalInMinutes !== 0) return false;
  }

  // 5. Verificar Ubicación de Pantalla (si está configurada)
  if (asset.targetGroups && asset.targetGroups.length > 0) {
    // Si la pantalla no tiene un grupo asignado y el anuncio exige uno, no lo mostramos
    if (!currentScreenGroup) return false;
    // Si la pantalla sí tiene un grupo pero no está en la lista de objetivos
    if (!asset.targetGroups.includes(currentScreenGroup)) return false;
  }

  // 6. Verificar si es exclusivo de una pantalla específica
  if (asset.targetScreenId) {
    // Si el anuncio es exclusivo para una TV y el currentScreenId no coincide, no se muestra
    if (!currentScreenId || asset.targetScreenId !== currentScreenId) return false;
  }

  return true;
};

export interface AdSettings {
  adsEnabled: boolean;                // Master toggle
  showAdsOnMobile: boolean;           // Toggle para celulares
  showAdsOnTV: boolean;               // Toggle para TVs
  hideAdsDuringSurgery: boolean;      // No mostrar ads cuando hay cirugía activa (FamilyView)
  tickerSpeed: 'slow' | 'normal' | 'fast';  // Velocidad del ticker
  tickerEnabled: boolean;             // Toggle del ticker
  fullscreenWhenEmpty: boolean;       // Activar slideshow fullscreen cuando no hay pacientes
  fullscreenIntervalSec: number;      // Intervalo entre slides fullscreen (default: 10)
}

export const DEFAULT_AD_SETTINGS: AdSettings = {
  adsEnabled: false,
  showAdsOnMobile: true,
  showAdsOnTV: true,
  hideAdsDuringSurgery: true,
  tickerSpeed: 'normal',
  tickerEnabled: true,
  fullscreenWhenEmpty: true,
  fullscreenIntervalSec: 10,
};

// Especificaciones de imagen por tipo de anuncio
export const AD_IMAGE_SPECS: Record<string, { width?: number; height?: number; maxSizeKB: number; label: string }> = {
  BANNER_IMAGE: { width: 1200, height: 120, maxSizeKB: 5000, label: 'Banner TV (1200x120px)' },
  BANNER_IMAGE_MOBILE: { maxSizeKB: 5000, label: 'Banner Móvil (Libre/Cuadrado recomendado)' }, // Nueva especificación
  FULLSCREEN_SLIDE: { width: 1920, height: 1080, maxSizeKB: 50000, label: 'Slide Fullscreen — Imagen o Video' },
};

export interface HospitalPreferences {
  autoDeleteHours: number;       // Horas antes de auto-eliminar datos de paciente (default: 3)
  allowFamilyNotifications: boolean; // Permitir notificaciones push a familias
  showProcedureName: boolean;    // Mostrar nombre del procedimiento a familias
  showPatientName: boolean;      // Mostrar nombre del paciente a familias
  requirePrivacyNotice?: boolean; // Requerir aceptación de aviso de privacidad (default: false)
  customWelcomeMessage?: string; // Mensaje personalizado en la vista familiar
  promotionalBanners?: PromotionalBanner[]; // Legacy: banners de texto simple
  adAssets?: AdAsset[];           // New: sistema de publicidad completo
  adSettings?: AdSettings;        // New: configuración de publicidad
  googleReviewUrl?: string;       // Link de Google Reviews para recolección post-cirugía
}

export interface HospitalGroup {
  id: string;
  name: string;
  handle: string;
  logoUrl?: string;
  createdAt: string;
}

export interface Hospital {
  id: string;
  groupId?: string;
  name: string;
  handle: string; // Unique identifier for the hospital's link (e.g., /h/muguerza)
  maxRooms: number;
  branding: HospitalBranding;
  totalPatients: number; // For billing
  totalStaff?: number; // Optional since it's only populated in the dashboard summary
  createdAt: string;

  // Datos de Contacto
  contactName?: string;       // Nombre del admin del hospital
  contactEmail?: string;      // Email de contacto
  contactPhone?: string;      // Teléfono
  address?: string;           // Dirección física

  // Configuración Operativa
  staffPin?: string;          // PIN de acceso para el staff (en lugar de hardcoded)
  features?: string[];        // Módulos activos para facturación (ej. 'ADS', 'API', 'FEEDBACK')

  // Preferencias
  preferences?: HospitalPreferences;
  globalAdAssets?: AdAsset[]; // Activos publicitarios globales heredados del grupo

  // Estado
  isActive?: boolean;         // Habilitar/deshabilitar hospital

  // Visibilidad pública
  publicVisibility?: 'public' | 'pin' | 'private';  // Control de acceso a la recepción digital
  receptionPin?: string;      // PIN de recepción para visitantes (4-6 dígitos)
}

export interface Patient {
  id: string; // Unique code e.g., ARN88
  hospitalId: string; // Scoping for multi-tenancy
  name: string; // Optional, internal use
  procedureName?: string; // e.g. "Artroscopia de rodilla"
  doctorName?: string; // e.g. "Dr. García López"
  room: string;
  currentStatus: PatientStatus;
  currentSubStatus?: SurgerySubStatus;
  currentCondition?: SurgeryCondition;
  surgeryProgress: number; // 0 to 100 integer
  logs: StatusLog[];
  admissionTime: string;
  dischargeTime?: string; // ISO string when status becomes EXIT
  lastEditedAt?: string; // ISO string when patient record is manually edited
  updatedAt?: string; // ISO string for optimistic locking
  isDoctorEnCamino?: boolean; // Flag to indicate doctor is coming to speak
}

export interface Room {
  id: string;
  hospitalId: string; // Scoping for multi-tenancy
  name: string;
  currentPatientId: string | null;
}

export const STATUS_CONFIG = {
  [PatientStatus.WAITING]: { label: 'REGISTRO / INGRESO', color: 'bg-blue-100', text: 'text-med-navy', step: 0, progress: 0 },
  // Using the palette's "Light" color for Preparation
  [PatientStatus.PREPARATION]: { label: 'PREOPERATORIO', color: 'bg-med-light', text: 'text-med-navy', step: 1, progress: 5 },
  // Using a subtle purple from Muguerza's aesthetic for Room Entry
  [PatientStatus.ROOM_ENTRY]: { label: 'INGRESO A QUIRÓFANO', color: 'bg-purple-100', text: 'text-purple-800', step: 2, progress: 10 },
  // Using Amber for Anesthesia (distinct but warm)
  [PatientStatus.ANESTHESIA]: { label: 'ANESTESIA INICIADA', color: 'bg-med-warning', text: 'text-white', step: 3, progress: 15 },
  // Using the palette's "Accent" color (#5B65DC) for Surgery
  [PatientStatus.SURGERY]: { label: 'CIRUGÍA EN PROCESO', color: 'bg-med-accent', text: 'text-white', step: 4, progress: 20 },
  // Using Emerald Green for Recovery
  [PatientStatus.RECOVERY]: { label: 'RECUPERACIÓN / DESPERTAR', color: 'bg-med-success', text: 'text-white', step: 5, progress: 100 },
  [PatientStatus.EXIT]: { label: 'SALIDA A HABITACIÓN', color: 'bg-slate-500', text: 'text-white', step: 6, progress: 100 },
};

export const SUB_STATUS_CONFIG = {
  [SurgerySubStatus.INCISION]: { label: 'Incisión / Inicio', baseProgress: 20 },
  [SurgerySubStatus.PROCEDURE]: { label: 'Procedimiento Principal', baseProgress: 30 },
  [SurgerySubStatus.CLOSING]: { label: 'Cierre / Sutura', baseProgress: 95 },
  [SurgerySubStatus.FINISHED]: { label: '4. Procedimiento finalizado', baseProgress: 100 },
};

export const CONDITION_CONFIG = {
  [SurgeryCondition.NORMAL]: { label: 'Tiempo Normal', message: 'Todo transcurre según lo previsto.', color: 'bg-med-success/10 text-med-navy' },
  [SurgeryCondition.DELAYED]: { label: 'Retraso Leve', message: 'El procedimiento se extiende un poco, pero todo está bajo control.', color: 'bg-med-warning/10 text-med-navy' },
  [SurgeryCondition.EXTENDED]: { label: 'Cirugía Extendida', message: 'La cirugía requiere más tiempo del habitual. El equipo está enfocado.', color: 'bg-orange-100 text-orange-900' },
};

// ============================================================
// 👥 STAFF & ROLES
// ============================================================

export type StaffRole = 'SUPER_ADMIN' | 'GROUP_ADMIN' | 'HOSPITAL_ADMIN' | 'FINANCIERO' | 'MARKETING' | 'OPERATIVO';

export const STAFF_ROLES: { value: StaffRole; label: string; description: string; color: string }[] = [
  {
    value: 'SUPER_ADMIN',
    label: 'Super Admin',
    description: 'Control de ecosistema',
    color: 'bg-neutral-800 text-neutral-100 border-neutral-700'
  },
  {
    value: 'GROUP_ADMIN',
    label: 'Admin de Grupo',
    description: 'Gestiona la cadena hospitalaria',
    color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
  },
  {
    value: 'HOSPITAL_ADMIN',
    label: 'Admin del Hospital',
    description: 'Gestiona el personal y tiene acceso a analytics',
    color: 'bg-purple-500/10 text-purple-400 border-purple-500/20'
  },
  {
    value: 'FINANCIERO',
    label: 'Financiero',
    description: 'Acceso a analytics y reportes del hospital',
    color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
  },
  {
    value: 'MARKETING',
    label: 'Marketing',
    description: 'Gestión de publicidad, métricas y campañas',
    color: 'bg-pink-500/10 text-pink-400 border-pink-500/20'
  },
  {
    value: 'OPERATIVO',
    label: 'Operativo',
    description: 'Ingresa y actualiza el estado de pacientes',
    color: 'bg-blue-500/10 text-blue-400 border-blue-500/20'
  },
];

export interface StaffUser {
  id: string;
  hospitalId?: string | null;
  groupId?: string | null;
  name: string;
  email?: string;      // Nuevo para Cloudflare Access Auth
  role: StaffRole;
  pin?: string;        // Opcional ahora (solo para Operativos)
  isActive: boolean;
  createdAt: string;
  createdBy?: string;
}

export interface AuditEntry {
  id: string;
  hospitalId: string;
  staffId: string;
  staffName: string;
  action: string;
  targetId?: string;
  detail?: string;
  timestamp: string;
}

// ============================================================
// 📺 SCREEN SESSIONS — Device Fleet Monitoring
// ============================================================

export interface ScreenSession {
  id: string;                  // UUID, persisted in localStorage of the TV
  hospitalId: string;
  screenName: string;          // E.g. "Sala Espera Piso 3"
  handle: string;              // Hospital handle for quick lookups
  userAgent?: string;
  resolution?: string;         // E.g. "1920x1080"
  theme?: 'dark' | 'light';
  ipAddress?: string;          // From CF-Connecting-IP
  browser?: string;            // Parsed: "Chrome 120", "Safari 17"
  os?: string;                 // Parsed: "Windows 10/11", "Tizen (Samsung TV)"
  forcedTheme?: string | null; // Admin override: 'dark' | 'light' | null
  screenGroup?: string | null; // Location locator: 'PB', 'Piso 2'
  screenMode?: 'BOARD' | 'FULL_ADS' | 'CLOCK' | 'MEDIA_URL';
  screenModeData?: string | null; // e.g. youtube link
  forceRefreshTs?: number | null; // timestamp to force window reload
  lastHeartbeat: string;       // ISO string
  firstSeen: string;           // ISO string
  isRegistered: boolean;
  // Computed (not stored in DB)
  isOnline?: boolean;          // lastHeartbeat within 2 hours
  hospitalName?: string;       // Joined from hospitals table
}

// ============================================================
// 🎬 YOUTUBE URL PARSER
// ============================================================

/** Parses a YouTube URL and returns an embeddable iframe src, or null if invalid. */
export const parseYouTubeUrl = (url: string): string | null => {
  if (!url) return null;
  try {
    const u = new URL(url);
    // Playlist: youtube.com/playlist?list=XXXXX
    const listId = u.searchParams.get('list');
    // Video: youtube.com/watch?v=XXXXX or youtu.be/XXXXX
    let videoId: string | null = null;
    if (u.hostname.includes('youtu.be')) {
      videoId = u.pathname.slice(1);
    } else if (u.hostname.includes('youtube.com')) {
      videoId = u.searchParams.get('v');
      if (!videoId && u.pathname.startsWith('/embed/')) {
        videoId = u.pathname.split('/embed/')[1]?.split('?')[0] || null;
      }
      if (!videoId && u.pathname.startsWith('/live/')) {
        videoId = u.pathname.split('/live/')[1]?.split('?')[0] || null;
      }
    }
    // Playlist embed
    if (listId) {
      return `https://www.youtube.com/embed/videoseries?list=${listId}&autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0`;
    }
    // Single video embed
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0`;
    }
    return null;
  } catch {
    return null;
  }
};

export interface DocAccessToken {
  id: string;
  hospitalId?: string | null;
  label: string;
  createdAt: string;
  isRevoked: number;
  createdBy: string;
}